import { type ProductEventEnvelope } from "../productAnalytics";
import {
  validateWebAnalyticsRemoteConfig,
  webSamplingDefaults,
} from "./analyticsRuntimePolicyContract.mjs";

type ProductEventName = ProductEventEnvelope["eventName"];

export interface WebAnalyticsRemoteConfig {
  enabled: boolean;
  serverZone: "EU";
  sampling?: Partial<Record<ProductEventName, number>>;
}

export type WebAnalyticsPolicyUpdateResult =
  | { accepted: true }
  | { accepted: false; reason: string };

interface LocalAnalyticsConfig {
  environment: ProductEventEnvelope["environment"];
  explicitlyEnabled: boolean;
  serverZone: string;
}

const disabledRemoteConfig: WebAnalyticsRemoteConfig = {
  enabled: false,
  serverZone: "EU",
};

const deterministicUnitInterval = (eventId: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < eventId.length; index += 1) {
    hash ^= eventId.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967296;
};

export class WebAnalyticsRuntimePolicy {
  private remote = disabledRemoteConfig;
  private readonly availabilityListeners = new Set<
    (allowed: boolean) => void
  >();

  constructor(private readonly local: LocalAnalyticsConfig) {}

  update(value: unknown): WebAnalyticsPolicyUpdateResult {
    const wasAllowed = this.providerAllowed;
    const validation = validateWebAnalyticsRemoteConfig(value);
    this.remote = validation.accepted
      ? (validation.config as WebAnalyticsRemoteConfig)
      : disabledRemoteConfig;
    const isAllowed = this.providerAllowed;
    if (wasAllowed !== isAllowed) {
      this.availabilityListeners.forEach((listener) => listener(isAllowed));
    }
    return validation.accepted
      ? { accepted: true }
      : { accepted: false, reason: validation.reason };
  }

  async refresh(load: () => Promise<unknown>): Promise<void> {
    try {
      this.update(await load());
    } catch {
      this.update(disabledRemoteConfig);
    }
  }

  allows(envelope: ProductEventEnvelope): boolean {
    if (!this.providerAllowed) return false;

    const rate =
      this.remote.sampling?.[envelope.eventName] ??
      webSamplingDefaults[envelope.eventName];
    if (rate === undefined) return false;
    if (rate === 0) return false;
    if (rate === 1) return true;
    return deterministicUnitInterval(envelope.eventId) < rate;
  }

  subscribeToAvailability(listener: (allowed: boolean) => void): () => void {
    this.availabilityListeners.add(listener);
    return () => this.availabilityListeners.delete(listener);
  }

  get collectionAvailable(): boolean {
    return this.providerAllowed;
  }

  get locallyEligible(): boolean {
    return (
      this.local.environment === "production" &&
      this.local.explicitlyEnabled &&
      this.local.serverZone === "EU"
    );
  }

  reset(): void {
    this.update(disabledRemoteConfig);
  }

  private get providerAllowed(): boolean {
    return (
      this.locallyEligible &&
      this.remote.enabled &&
      this.remote.serverZone === "EU"
    );
  }
}

interface WebAnalyticsRuntimePolicyRefresherOptions {
  policy: WebAnalyticsRuntimePolicy;
  load: () => Promise<unknown>;
  refreshIntervalMs: number;
  scheduleRepeating: (intervalMs: number, action: () => void) => () => void;
  subscribeToActiveSession: (action: () => void) => () => void;
}

/** Keeps a running browser session within a bounded runtime-policy age. */
export class WebAnalyticsRuntimePolicyRefresher {
  private started = false;
  private refreshing = false;
  private refreshPending = false;
  private generation = 0;
  private stopInterval: (() => void) | undefined;
  private stopActiveSubscription: (() => void) | undefined;

  constructor(
    private readonly options: WebAnalyticsRuntimePolicyRefresherOptions,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    this.stopInterval = this.options.scheduleRepeating(
      this.options.refreshIntervalMs,
      () => this.requestRefresh(),
    );
    this.stopActiveSubscription = this.options.subscribeToActiveSession(() =>
      this.requestRefresh(),
    );
    this.requestRefresh();
  }

  stop(): void {
    this.started = false;
    this.refreshPending = false;
    this.generation += 1;
    this.stopInterval?.();
    this.stopActiveSubscription?.();
    this.stopInterval = undefined;
    this.stopActiveSubscription = undefined;
  }

  private requestRefresh(): void {
    if (!this.started) return;
    if (this.refreshing) {
      this.refreshPending = true;
      return;
    }

    this.refreshing = true;
    const generation = this.generation;
    void this.refresh(generation).finally(() => {
      this.refreshing = false;
      if (!this.started || !this.refreshPending) return;
      this.refreshPending = false;
      this.requestRefresh();
    });
  }

  private async refresh(generation: number): Promise<void> {
    try {
      const value = await this.options.load();
      if (this.started && generation === this.generation) {
        this.options.policy.update(value);
      }
    } catch {
      if (this.started && generation === this.generation) {
        this.options.policy.reset();
      }
    }
  }
}

export const loadWebAnalyticsRemoteConfig = async (
  url: string,
): Promise<unknown> => {
  const response = await fetch(url, {
    cache: "no-store",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error("Product Analytics policy unavailable");
  return response.json();
};
